import {
  NAV_GUARD_DEADLINE_KEY,
  NAV_GUARD_HANDLED_KEY,
  NAV_GUARD_HYDRATED_KEY,
  NAV_GUARD_UNSTICK_MS,
} from "./document-nav-guard";

/**
 * COM-67 — the pre-hydration half of the re-entrant navigation guard.
 *
 * `document-nav-guard.ts` is a React `onClick`, so it does not exist until
 * hydration. The server-rendered `<a href>` is clickable the moment it parses.
 * Measured at CPU 4x / 300ms RTT: links become clickable at +5,798ms, the first
 * guarded anchor at +6,337ms — a window of roughly 0.3-0.8s per page load in
 * which NO full-document link is guarded.
 *
 * That window is load-bearing, not cosmetic. Preview testing on 2026-09-03:
 * with only the React-side guard, a SAME-ORIGIN link storm produced 4-7
 * document navigations per 12 clicks and 2 permanent wedges in 8 trials —
 * because the first navigation COMMITS, the document unloads, the React guard's
 * state dies with it, and clicks 2..N land in the next document's
 * pre-hydration window. (The cross-origin case never shows this: the navigation
 * leaves the site, so there is no second document. It measured a clean 12
 * clicks -> 1 navigation on 6/6 trials.)
 *
 * ## Ordering: why `defaultPrevented` cannot be used to detect a SPA click
 *
 * The obvious design — bubble phase on `document`, bypass anything already
 * `defaultPrevented`, assuming React Router's `Link` has run first — is WRONG
 * here, and it took two browser tests to establish why.
 *
 * `entry.client.tsx` does `hydrateRoot(document, <HydratedRouter />)`, so React
 * attaches its listeners to **`document`** — the same node as this listener.
 * Two bubble listeners on one node fire in REGISTRATION order, and this one is
 * registered at `<head>` parse time, long before React hydrates. So it runs
 * FIRST, and `defaultPrevented` is still false even for a click that `Link` is
 * about to turn into a client-side navigation. Measured directly: the flag is
 * false in this listener and true in one registered after React's.
 *
 * Consequence when that was got wrong: a product-tile SPA click armed the
 * guard, and the site-wide alert-bar CTA clicked 1.2s later was silently
 * swallowed — `documentRequests=0`, URL unchanged. A dead link, for 8 seconds.
 *
 * Deferring the arm to a `queueMicrotask` and revoking it if the click turned
 * out to be `defaultPrevented` was tried next. It passed in happy-dom and still
 * failed in the browser. So this listener does not try to detect SPA-ness.
 *
 * ## The ownership rule used instead
 *
 * Split on hydration state, because that is what actually determines who is
 * able to guard a click:
 *
 *  - **Before hydration** there is no React, so NO SPA navigation is possible:
 *    every anchor click is a full-document navigation. This listener is the
 *    only guard in existence, so it handles everything. This is the window
 *    that matters — it is where the wedge reproduced.
 *  - **After hydration** React owns every anchor it rendered: `LinkRender`,
 *    `GuardedBuilderLink`, `HybridLink` / `NavLink` (when they resolve to
 *    `reloadDocument`) and the account-drawer's raw checkout anchors carry
 *    `handleDocumentNavClick`, and react-router `Link` owns its own SPA
 *    clicks. So this listener steps back to only the anchors React does NOT
 *    own — raw `<a>` inside embedded HTML / rich text, which nothing else
 *    guards. A React-rendered anchor always carries a `__reactProps$` key,
 *    which is the test used.
 *
 *    The corollary is the contract every React-rendered FULL-DOCUMENT anchor
 *    must honour: it has to carry the guard itself, because this listener will
 *    not. A react-router `Link reloadDocument` does NOT get one for free —
 *    RR hands its clicks straight to the caller's `onClick` — which is why
 *    `HybridLink` / `NavLink` compose it in (COM-67 / COM-405: the cart and
 *    minicart "Continue to Checkout" CTAs were unguarded for exactly this
 *    reason). `tests/unit/hec/components/reload-document-guard-coverage.test.ts`
 *    is the drift guard.
 *
 * `document-nav-guard.ts` sets `window.__hecNavGuardHydrated` when the client
 * bundle evaluates; that is the hand-off signal.
 *
 * `preventDefault()` during bubbling still cancels the anchor's navigation:
 * the default action runs after the full dispatch completes.
 *
 * The deadline is shared with the React side via `window[NAV_GUARD_DEADLINE_KEY]`
 * so the two owners cannot disagree about whether a navigation is in flight.
 *
 * Kept deliberately small and dependency-free — it is inline in `<head>` on
 * every page, ahead of hydration, and must not delay anything.
 */
export const DOCUMENT_NAV_GUARD_INLINE_SOURCE = `
(function () {
  var D = ${JSON.stringify(NAV_GUARD_DEADLINE_KEY)};
  var H = ${JSON.stringify(NAV_GUARD_HANDLED_KEY)};
  var Y = ${JSON.stringify(NAV_GUARD_HYDRATED_KEY)};
  var TTL = ${NAV_GUARD_UNSTICK_MS};
  if (window[D] === undefined) window[D] = 0;

  document.addEventListener("click", function (e) {
    // Already cancelled by something that ran before us (an inner-element
    // handler, or the Builder editor's link guard). No document load will start.
    if (e.defaultPrevented) return;
    // A React onClick on the anchor already decided this exact click.
    if (e[H]) return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var t = e.target;
    if (!t || typeof t.closest !== "function") return;
    var a = t.closest("a[href]");
    if (!a) return;
    if (a.hasAttribute("download")) return;
    var tgt = a.getAttribute("target");
    if (tgt && tgt !== "_self") return;

    var raw = a.getAttribute("href") || "";
    if (!raw) return;
    var lower = raw.toLowerCase();
    if (
      lower.charAt(0) === "#" ||
      lower.indexOf("mailto:") === 0 ||
      lower.indexOf("tel:") === 0 ||
      lower.indexOf("javascript:") === 0
    ) {
      return;
    }
    // Same-document hash jump: not a navigation.
    try {
      if (a.pathname === location.pathname && a.search === location.search && a.hash) return;
    } catch (err) {}

    // OWNERSHIP RULE (see the docblock). Pre-hydration this listener guards
    // everything, because no SPA navigation is possible. Post-hydration it
    // steps back to anchors React did not render -- React owns the rest, and
    // trying to second-guess a react-router Link from here cannot work.
    if (window[Y]) {
      for (var k in a) {
        if (k.lastIndexOf("__reactProps$", 0) === 0) return;
      }
    }

    e[H] = 1;
    var now = Date.now();
    if (now < window[D]) {
      e.preventDefault();
      return;
    }
    window[D] = now + TTL;
  });

  // bfcache restore revives this document with the deadline still set.
  window.addEventListener("pageshow", function (e) {
    if (e.persisted) window[D] = 0;
  });
})();
`;
